import { createFileRoute, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import useWebSocket from 'react-use-websocket';
import { z } from 'zod';
import { Button } from '~frontend/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~frontend/components/ui/select';
import { getBaseBackendUrl } from '~frontend/lib/utils';

const mobileCameraSearchSchema = z.object({
    sessionId: z.string().optional(),
})

interface WebSocketMessage {
    type: 'image' | 'status' | 'confirmation' | 'error';
    payload: any;
}


function getWebSocketUrl(sessionId: string) {
    const baseUrl = getBaseBackendUrl();
    console.log('Mobile - Base URL for WebSocket:', baseUrl);
    const url = new URL(baseUrl);
    const wsUrl = url.origin.replace(/^http/, 'ws');
    // Remove type parameter - let backend determine from referrer or route
    const finalWsUrl = `${wsUrl}/mobile-camera/ws?sessionId=${sessionId}`;
    console.log('Mobile - Final WebSocket URL:', finalWsUrl);
    return finalWsUrl;
}

export const Route = createFileRoute('/_LoggedIn/cadet/mobile-camera')({
    component: MobileCamera,
    validateSearch: (search) => mobileCameraSearchSchema.parse(search),
});

function MobileCamera() {
    const { sessionId } = useSearch({ from: '/_LoggedIn/cadet/mobile-camera' });
    console.log('Mobile camera using session ID:', sessionId);

    const [stream, setStream] = useState<MediaStream | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isSending, setIsSending] = useState(false);
    const [justSent, setJustSent] = useState(false);
    const [isSecureContext, setIsSecureContext] = useState(false);
    const [capturedImage, setCapturedImage] = useState<string | null>(null);

    // Camera selection state
    const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
    const [selectedCameraId, setSelectedCameraId] = useState<string | undefined>(undefined);

    const { sendJsonMessage, lastJsonMessage, readyState } = useWebSocket<WebSocketMessage>(
        sessionId ? getWebSocketUrl(sessionId) : null,
        {
            shouldReconnect: () => true,
            onOpen: () => {
                console.log('Mobile WebSocket connected for session:', sessionId);
            },
            onClose: (event) => {
                console.log('Mobile WebSocket closed:', event.code, event.reason);
            },
            onError: (event) => {
                console.error('Mobile WebSocket error:', event);
            },
            onMessage: (event) => {
                console.log('Mobile received message:', event.data);
            }
        }
    );

    // Handle incoming messages from desktop
    useEffect(() => {
        if (lastJsonMessage) {
            console.log('Mobile received message:', lastJsonMessage);
            switch (lastJsonMessage.type) {
                case 'confirmation':
                    console.log('Desktop confirmed photo delivery:', lastJsonMessage.payload);
                    break;
                case 'status':
                    console.log('Desktop status update:', lastJsonMessage.payload);
                    if (lastJsonMessage.payload.status === 'received') {
                        // Photo was successfully received by desktop
                        setJustSent(true);
                        setTimeout(() => setJustSent(false), 3000);
                    }
                    break;
                case 'error':
                    console.error('Error from desktop:', lastJsonMessage.payload);
                    alert(`Error: ${lastJsonMessage.payload.error}`);
                    break;
            }
        }
    }, [lastJsonMessage]);

    useEffect(() => {
        setIsSecureContext(window.isSecureContext);
    }, []);

    // Effect to attach stream to video element when both are available
    useEffect(() => {
        if (stream && videoRef.current) {
            console.log("Attaching stream to video element");
            videoRef.current.srcObject = stream;
            videoRef.current.play().catch(e => console.error("Video play failed:", e));
        }
    }, [stream]);

    // Fetch available cameras and keep list updated
    useEffect(() => {
        const fetchCameras = async () => {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const videoInputs = devices.filter((d) => d.kind === 'videoinput');
                setCameras(videoInputs);

                // Auto-select first camera if none selected yet
                if (!selectedCameraId && videoInputs.length > 0) {
                    setSelectedCameraId(videoInputs[0].deviceId);
                }
            } catch (err) {
                console.error('Error fetching cameras:', err);
            }
        };

        fetchCameras();
        navigator.mediaDevices.addEventListener('devicechange', fetchCameras);
        return () => navigator.mediaDevices.removeEventListener('devicechange', fetchCameras);
    }, []); // run once on mount

    const handleCameraChange = (deviceId: string) => {
        setSelectedCameraId(deviceId);

        // If camera is already streaming, switch immediately
        if (stream) {
            stopCamera();
            startCamera(deviceId);
        }
    };

    const startCamera = async (deviceIdOverride?: string) => {
        if (!isSecureContext) {
            console.error("Camera access is only available in secure contexts (HTTPS).");
            return;
        }
        try {
            console.log("Attempting to start camera...");
            const deviceIdToUse = deviceIdOverride ?? selectedCameraId;
            const constraints: MediaStreamConstraints = deviceIdToUse
                ? { video: { deviceId: { exact: deviceIdToUse } } }
                : { video: { facingMode: { ideal: 'environment' } } };

            const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
            console.log("Camera stream acquired:", mediaStream);
            setStream(mediaStream);

            // Refresh camera list now that permission is granted (labels become available)
            navigator.mediaDevices.enumerateDevices()
                .then((devices) => setCameras(devices.filter((d) => d.kind === 'videoinput')))
                .catch((err) => console.error('Error refreshing camera list:', err));

            // The useEffect above will handle attaching to video element
        } catch (err) {
            console.error("Error accessing camera: ", err);
        }
    };

    const stopCamera = () => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
    };

    useEffect(() => {
        // Only cleanup function - no automatic camera start
        return () => {
            if (stream) {
                stopCamera();
            }
        };
    }, [stream]);

    const downloadImage = (dataUrl: string) => {
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = `enrollment-capture-${sessionId}-${Date.now()}.jpg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const takePhoto = () => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');
            if (context) {
                const { videoWidth, videoHeight } = video;

                // Calculate cropping dimensions for a square image
                const size = Math.min(videoWidth, videoHeight);
                const sx = (videoWidth - size) / 2;
                const sy = (videoHeight - size) / 2;

                // Set canvas to be square
                canvas.width = size;
                canvas.height = size;

                // Draw the cropped section of the video onto the canvas
                context.drawImage(
                    video,
                    sx,
                    sy,
                    size,
                    size,
                    0,
                    0,
                    size,
                    size
                );

                const dataUrl = canvas.toDataURL('image/jpeg', 0.8);

                setCapturedImage(dataUrl);
                downloadImage(dataUrl);
                // Don't stop the camera anymore - keep it running for next photo
            }
        }
    };

    const handleSendPhoto = () => {
        if (!capturedImage) return;

        console.log('Sending photo via WebSocket...', {
            sessionId,
            readyState,
            capturedImageLength: capturedImage.length
        });

        setIsSending(true);

        const message = {
            type: 'image',
            payload: capturedImage.split(',')[1] // send base64 part
        };

        console.log('Sending message:', message);
        sendJsonMessage(message);

        // Send photo and provide feedback
        setTimeout(() => {
            setIsSending(false);
            setCapturedImage(null); // Clear the captured image
            stopCamera(); // Stop the camera after sending
            console.log('Photo sent, camera stopped');

            // Note: We now wait for desktop confirmation before showing success
        }, 1000);
    };

    const handleRetake = () => {
        setCapturedImage(null);
        // Camera stream is still active, so we just return to camera view
    };

    if (!sessionId) {
        return <div className="p-4 text-red-500">Error: No session ID provided.</div>;
    }

    if (!isSecureContext) {
        return (
            <div className="p-4 text-center text-red-500">
                <b>Camera access is disabled.</b>
                <p>Please use a secure connection (HTTPS) to enable the camera.</p>
            </div>
        );
    }

    if (capturedImage) {
        return (
            <div className="w-screen h-screen bg-black flex flex-col items-center justify-center p-4">
                <div className="w-full max-w-lg mx-auto aspect-square relative">
                    <img src={capturedImage} alt="Captured preview" className="w-full h-full object-cover rounded-lg" />
                </div>
                <div className="mt-4 flex justify-center gap-4">
                    <Button onClick={handleRetake}>Retake</Button>
                    <Button onClick={handleSendPhoto} disabled={isSending}>
                        {isSending ? 'Sending...' : 'Send Photo'}
                    </Button>
                </div>
            </div>
        )
    }

    // Show start camera button when there's no stream
    if (!stream) {
        return (
            <div className="w-screen h-screen bg-black flex flex-col items-center justify-center">
                {justSent && (
                    <div className="mb-4 bg-green-600 text-white px-6 py-3 rounded-lg">
                        Photo sent successfully! Check the enrollment page.
                    </div>
                )}

                {/* Debug section */}
                <div className="absolute top-4 left-4 right-4 bg-gray-800 text-white p-2 rounded text-xs space-y-1">
                    <div><strong>Mobile Debug:</strong></div>
                    <div>Session: <code>{sessionId}</code></div>
                    <div>Status: <span className={readyState === WebSocket.OPEN ? 'text-green-400' : 'text-yellow-400'}>
                        {readyState === WebSocket.OPEN ? '🟢 Connected' : '🟡 Connecting...'}
                    </span></div>
                </div>

                <div className="mb-4 text-center">
                    <div className={`inline-block px-3 py-1 rounded-full text-sm ${readyState === WebSocket.OPEN
                        ? 'bg-green-600 text-white'
                        : 'bg-yellow-600 text-white'
                        }`}>
                        {readyState === WebSocket.OPEN ? '🟢 Connected' : '🟡 Connecting...'}
                    </div>
                </div>

                {/* Camera selector */}
                {cameras.length > 1 && (
                    <div className="mb-4 text-gray-50 ">
                        <Select value={selectedCameraId} onValueChange={handleCameraChange}>
                            <SelectTrigger className="w-48">
                                <SelectValue placeholder="Select Camera" />
                            </SelectTrigger>
                            <SelectContent>
                                {cameras.map((cam, idx) => (
                                    <SelectItem key={cam.deviceId} value={cam.deviceId}>
                                        {cam.label || `Camera ${idx + 1}`}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}

                <Button
                    onClick={() => startCamera(selectedCameraId)}
                    className="px-8 py-4 text-lg"
                    disabled={readyState !== WebSocket.OPEN}
                >
                    Start Camera
                </Button>
            </div>
        )
    }

    // Show camera interface when stream is active
    return (
        <div className="w-screen h-screen bg-black flex flex-col items-center justify-center p-4">
            {justSent && (
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-4 py-2 rounded-lg z-10">
                    Photo sent successfully!
                </div>
            )}

            <div className="absolute top-4 right-4 text-xs text-gray-400">
                Session: {sessionId}
            </div>

            <div className="w-full max-w-lg mx-auto aspect-square relative">
                <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover rounded-lg"
                />
                <canvas ref={canvasRef} className="hidden"></canvas>
            </div>

            {/* Camera selector when stream is active */}
            {cameras.length > 1 && (
                <div className="absolute top-4 left-4 z-20">
                    <Select value={selectedCameraId} onValueChange={handleCameraChange}>
                        <SelectTrigger className="w-40">
                            <SelectValue placeholder="Camera" />
                        </SelectTrigger>
                        <SelectContent>
                            {cameras.map((cam, idx) => (
                                <SelectItem key={cam.deviceId} value={cam.deviceId}>
                                    {cam.label || `Camera ${idx + 1}`}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}

            <div className="mt-4 flex justify-center gap-4">
                <Button
                    onClick={stopCamera}
                    variant="outline"
                    className="px-4 py-2"
                >
                    Stop Camera
                </Button>
                <Button
                    onClick={takePhoto}
                    disabled={!stream || isSending || readyState !== WebSocket.OPEN}
                    className="w-20 h-20 rounded-full bg-white text-black hover:bg-gray-200"
                >
                    {isSending ? '...' : 'Snap'}
                </Button>
            </div>
        </div>
    );
} 